import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MarketplaceCatalogItem } from '../../models/marketplace-catalog-item.model';
import { MarketplaceCatalogService } from '../../services/marketplace-catalog.service';

@Component({
  selector: 'app-marketplace-catalog',
  templateUrl: './marketplace-catalog.component.html',
  styleUrls: ['./marketplace-catalog.component.css'],
  standalone: false
})
export class MarketplaceCatalogComponent implements OnInit, OnDestroy {
  /** Matches the server-enforced 12-row ceiling on admin lists. */
  readonly pageSize = 12;

  isLoading = false;
  items: MarketplaceCatalogItem[] = [];
  page = 1;
  totalCount = 0;
  totalPages = 1;
  searchTerm = '';
  activeOnly = false;
  errorMessage = '';

  private readonly searchSubject = new Subject<string>();
  private readonly subs = new Subscription();

  constructor(private readonly service: MarketplaceCatalogService) {}

  ngOnInit(): void {
    this.subs.add(
      this.searchSubject.pipe(debounceTime(350), distinctUntilChanged()).subscribe(() => {
        this.page = 1;
        this.load();
      })
    );
    this.load();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSearch(term: string) {
    this.searchSubject.next(term);
  }

  toggleActiveOnly(value: boolean) {
    this.activeOnly = value;
    this.page = 1;
    this.load();
  }

  setPage(page: number) {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.page = page;
    this.load();
  }

  get rangeLabel(): string {
    if (!this.totalCount) {
      return '0';
    }
    const start = (this.page - 1) * this.pageSize + 1;
    const end = Math.min(this.page * this.pageSize, this.totalCount);
    return `${start}–${end} of ${this.totalCount}`;
  }

  private load() {
    this.isLoading = true;
    this.errorMessage = '';
    this.service.getProducts(this.page, this.pageSize, this.searchTerm, undefined, this.activeOnly).subscribe({
      next: result => {
        this.isLoading = false;
        this.items = result?.data ?? [];
        this.totalCount = result?.totalCount ?? 0;
        this.totalPages = Math.max(result?.totalPages ?? 1, 1);
      },
      error: err => {
        this.isLoading = false;
        this.items = [];
        this.errorMessage = err?.message || 'Loading the marketplace catalog failed.';
      }
    });
  }
}
